import {
  GrowthBook,
  InitOptions,
  Options,
  TrackingCallback,
} from "@growthbook/growthbook";
import Cookies from "js-cookie";
import { MmGbState } from "./mm-gb-state";
import { gbViewedExperimentDataLayerEventName } from "./constants";

/**
 * Main client options and init.
 */
export function createGrowthbookClient(
  gbState: MmGbState,
  options?: Options,
  customTrackingCallback?: TrackingCallback,
) {
  const finalAttributes = {
    ...gbState?.a,
  };

  let enableDevMode = process.env.NODE_ENV !== "production";
  if (typeof document !== "undefined") {
    // Get the user ID instead of the wrong one that will be cached with
    // the HTML response.
    finalAttributes.id = Cookies.get("gbuuid");

    // Enable dev mode for local development and on test environments.
    enableDevMode =
      enableDevMode || window.location.hostname.includes(".run.app");
  }

  return new GrowthBook({
    apiHost: "https://cdn.growthbook.io",
    enableDevMode,

    trackingCallback: (experiment, result) => {
      customTrackingCallback?.(experiment, result);

      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: gbViewedExperimentDataLayerEventName,
        growthbook: {
          experimentId: experiment.key,
          variationId: result.key,
        },
      });
    },

    // Targeting attributes
    attributes: finalAttributes,

    ...options,
  });
}

/**
 * When there is a payload for GB and it can be initialized synchronously.
 */
export const createSyncGrowthbookClient = (
  gbState: MmGbState,
  options?: Options,
  initOptions?: { streaming?: boolean },
  customTrackingCallback?: TrackingCallback,
) => {
  if (!options?.clientKey) {
    return null;
  }

  return createGrowthbookClient(
    gbState,
    options,
    customTrackingCallback,
  ).initSync({
    payload: gbState!.p!,
    ...initOptions,
  });
};

/**
 * When there is no payload for GB and it has to be initialized from scratch.
 */
export const createAsyncGrowthbookClient = async (
  gbState: MmGbState,
  options?: Options,
  initOptions?: InitOptions,
  customTrackingCallback?: TrackingCallback,
) => {
  const client = createGrowthbookClient(
    gbState,
    options,
    customTrackingCallback,
  );

  // init() does calls over the internet
  await client.init(initOptions);

  return client;
};
